import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { businessNumberLabel } from '@/lib/locale'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Printer, Lock, Send } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useCompany } from '@/hooks/use-company'
import { useClients, clientName } from '@/hooks/use-clients'
import { Page, DetailSkeleton, btnSecondary, btnPrimary } from '@/components/rm'
import type { ComplianceDocument } from '@/hooks/use-compliance'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/compliance_/$id')({ component: CertificatePage })

const fmtDate = (iso: string) =>
  phFormat(new Date(iso), 'date', { day: 'numeric', month: 'long', year: 'numeric' })

/** A signature field stores a data URL; everything else renders as text. */
const isSignature = (v: unknown) => typeof v === 'string' && v.startsWith('data:image')

function CertificatePage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { company } = useCompany()
  const { clients } = useClients()
  const [sending, setSending] = useState(false)

  const q = useQuery({
    queryKey: ['compliance-doc', id],
    queryFn: async (): Promise<ComplianceDocument | null> => {
      const { data, error } = await supabase.from('compliance_document').select('*').eq('id', id).maybeSingle()
      if (error) throw error
      return data as ComplianceDocument | null
    },
  })

  if (q.isLoading) return <Page><DetailSkeleton /></Page>

  if (q.isError) {
    return (
      <Page>
        <p className="font-display text-[18px] font-bold text-token-primary">Couldn’t load this certificate</p>
        <p className="mt-1 text-[13px] text-token-secondary">{(q.error as Error)?.message}</p>
        <button className={btnSecondary + ' mt-4'} onClick={() => q.refetch()}>Try again</button>
      </Page>
    )
  }
  if (!q.data) {
    return (
      <Page>
        <p className="font-display text-[18px] font-bold text-token-primary">Certificate not found</p>
        <button className={btnSecondary + ' mt-4'} onClick={() => navigate({ to: '/compliance' })}>
          <ArrowLeft className="h-4 w-4" /> Back to compliance
        </button>
      </Page>
    )
  }

  const d = q.data
  const accent = company?.accent_color || '#0038a8'
  const coName = (company?.name || 'Your business').trim()
  const client = clients.find((c) => c.id === d.contact_id)
  // Everything below the header comes from the FROZEN payload, never from live rows. That is what
  // makes this reproducible: the same record always renders the same certificate.
  const entries = Object.entries(d.payload ?? {})

  return (
    <Page>
      <div className="mb-4 flex items-center justify-between print:hidden">
        <button
          onClick={() => navigate({ to: '/compliance' })}
          className="inline-flex items-center gap-1 text-[13px] font-medium text-token-secondary transition-colors hover:text-token-secondary"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Compliance
        </button>
        <div className="flex gap-2">
          <button className={btnSecondary} onClick={() => window.print()}>
            <Printer className="h-4 w-4" /> Print / PDF
          </button>
          <button
            className={btnPrimary}
            disabled={sending}
            onClick={async () => {
              setSending(true)
              const { data, error } = await supabase.functions.invoke('send-compliance-doc', {
                body: { docId: id, origin: window.location.origin },
              })
              setSending(false)
              const res = data as { ok?: boolean; sentTo?: string } | null
              if (error || !res?.ok) {
                // The server knows WHY (no email on file, SES down) — surface its message.
                const ctx = (error as { context?: Response } | null)?.context
                let msg = 'Could not send the certificate'
                try { if (ctx) { const j = await ctx.json(); if (j?.error) msg = j.error } } catch { /* */ }
                return toast.error(msg)
              }
              toast.success(`Sent to ${res.sentTo}`)
              q.refetch()
            }}
          >
            <Send className="h-4 w-4" /> {sending ? 'Sending…' : 'Send to customer'}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl rounded-[16px] border border-token-subtle bg-token-surface p-7 print:max-w-none print:rounded-none print:border-0 print:p-0">
        {/* Letterhead */}
        <div className="flex items-start justify-between gap-6 border-b border-token-subtle pb-5">
          <div className="flex items-center gap-3">
            {company?.logo
              ? <img src={company.logo} alt={coName} className="h-12 w-12 rounded-[10px] object-cover" />
              : <span className="flex h-12 w-12 items-center justify-center rounded-[10px] text-[20px] font-bold text-white" style={{ background: accent }}>{coName[0]?.toUpperCase()}</span>}
            <div>
              <p className="font-display text-[19px] font-bold tracking-[-0.02em] text-token-primary">{coName}</p>
              {company?.abn && <p className="text-[12px] text-token-secondary">{businessNumberLabel(company)} {company.abn}</p>}
              {company?.phone && <p className="text-[12px] text-token-secondary">{company.phone}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">{d.doc_type}</p>
            <p className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{d.doc_number}</p>
            <p className="text-[12px] text-token-secondary">Issued {fmtDate(d.issued_at)}</p>
          </div>
        </div>

        {(client || d.licence_number) && (
          <div className="grid grid-cols-2 gap-6 border-b border-token-subtle py-5">
            {client && (
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-token-secondary">Issued to</p>
                <p className="mt-1 text-[14px] font-semibold text-token-primary">{clientName(client)}</p>
              </div>
            )}
            {d.licence_number && (
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-token-secondary">Licence number</p>
                <p className="mt-1 text-[14px] font-semibold text-token-primary">{d.licence_number}</p>
              </div>
            )}
          </div>
        )}

        {/* The record, exactly as signed */}
        <div className="py-5">
          {entries.length === 0 ? (
            <p className="text-[13px] text-token-secondary">This certificate has no recorded details.</p>
          ) : (
            <dl className="space-y-2.5">
              {entries.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[38%_62%] gap-4 border-b border-token-subtle pb-2.5 last:border-0">
                  <dt className="text-[12.5px] text-token-secondary">{k}</dt>
                  <dd className="text-[13.5px] text-token-primary">
                    {isSignature(v) ? (
                      <img src={v as string} alt={`${k} signature`} className="h-16 object-contain" />
                    ) : typeof v === 'boolean' ? (
                      v ? 'Yes' : 'No'
                    ) : (
                      String(v ?? '—')
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {d.sent_at && (
          <p className="mt-4 rounded-[8px] bg-token-success-surface px-3 py-2 text-[12px] font-semibold text-token-success print:hidden">
            Sent to {d.sent_to} on {fmtDate(d.sent_at)}
          </p>
        )}

        {d.signed_at && (
          <p className="flex items-center gap-1.5 border-t border-token-subtle pt-4 text-[12px] text-token-secondary">
            <Lock className="h-3.5 w-3.5 text-token-secondary" />
            Signed and locked {fmtDate(d.signed_at)}. This record cannot be altered; any correction is
            issued as a new certificate.
          </p>
        )}

        <p className="mt-4 text-[11px] leading-relaxed text-token-muted">
          {coName} is responsible for the accuracy of this certificate and for meeting the requirements of
          the relevant state or territory and its licence conditions.
        </p>
      </div>
    </Page>
  )
}
